import classNames from 'classnames/bind';
import React, { HTMLProps, MutableRefObject, forwardRef, useEffect, useRef } from 'react';

import Styles from './checkbox.module.scss';

const cx = classNames.bind(Styles);

/*
  Accessibility Notes:
  https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/checkbox_role

  - Expose the element as having a checkbox role in the operating system's accessibility API.
  - aria-checked attribute needs to be up to date with the state of our checkbox.
  - Ensure that tab-index='0'; Used to make it focusable so the assistive technology user can tab to it and start reading right away.

  Helpful source:
  https://webdesign.tutsplus.com/tutorials/how-to-make-custom-accessible-checkboxes-and-radio-buttons--cms-32074
*/

interface Props extends HTMLProps<HTMLInputElement> {
  children?: React.ReactNode;
  indeterminate?: boolean;
  stacked?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void; // Weird bug, HTMLProps types onChange as a FormEvent when it should be a ChangeEvent
}

const Checkbox = forwardRef<HTMLInputElement, Props>(
  ({ children, disabled = false, stacked = false, indeterminate, value, ...otherProps }, ref) => {
    if (!ref) {
      ref = useRef(null);
    }

    useEffect(() => {
      if (indeterminate !== undefined) {
        const inputRef: MutableRefObject<HTMLInputElement> = ref as MutableRefObject<HTMLInputElement>;

        if (inputRef && inputRef.current) {
          inputRef.current.indeterminate = indeterminate;
        }
      }
    }, [indeterminate]);

    return (
      <label
        className={cx(
          'form-element',
          'checkbox-wrapper',
          { disabled: disabled },
          { stacked: stacked },
          { fw: stacked },
        )}>
        <input
          type='checkbox'
          value={value}
          ref={ref}
          {...otherProps}
          tabIndex={0}
          disabled={disabled}
          role='checkbox'
        />
        <span tabIndex={0}>{children}</span>
      </label>
    );
  },
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
